import { useCallback, useState, type ReactElement } from 'react'
import { createId } from '@util'
import {
  isDefined,
  isNotDefined,
  type DashboardFilter,
  type DashboardWidget,
  type DashboardWidgetId,
  type DashboardWidgetType,
} from '@terros/common'
import { createFileRoute } from '@tanstack/react-router'
import { useDisclosure } from '@mantine/hooks'
import { Stack } from '@mantine/core'
import { DEFAULT_DASHBOARD_DATE_RANGE, useTranslation } from '@front/model'
import { ButtonCancel, ButtonSave, DangerZone, Page, TButton } from '@components'
import { WidgetConfigModal } from './widgetBuilder/WidgetConfigModal'
import { SplitPreviewLayout } from './SplitPreviewLayout'
import { HustleWidgetSection } from './HustleWidgetSection'
import { DashboardWidgetListSection } from './DashboardWidgetListSection'
import { DashboardWidgetCopyPicker } from './DashboardWidgetCopyPicker'
import { DashboardSettingsSection } from './DashboardSettingsSection'
import { useDashboardDraft } from './dashboardDraftContext'
import { DashboardBuildPreview } from './DashboardBuildPreview'

/** The 2b build screen: form on the left, live dashboard preview on the right. */
function DashboardBuild(): ReactElement {
  const { t } = useTranslation()
  const { form, dashboard, reportColumns, isSaving, isRemoving, save, cancel, remove } = useDashboardDraft()
  const [isCopyOpen, { open: openCopy, close: closeCopy }] = useDisclosure(false)
  const [editingWidgetId, setEditingWidgetId] = useState<DashboardWidgetId | undefined>(undefined)
  const editingWidget = form.values.widgets.find((widget) => widget.widgetId === editingWidgetId)

  const openWidget = useCallback((widget: DashboardWidget): void => {
    setEditingWidgetId(widget.widgetId)
  }, [])

  const closeWidget = useCallback((): void => setEditingWidgetId(undefined), [])

  const addWidget = useCallback(
    (type: DashboardWidgetType): void => {
      const widget = createWidget(type)
      form.insertListItem('widgets', widget)
      openWidget(widget)
    },
    [form, openWidget]
  )

  const duplicateWidget = useCallback(
    (index: number): void => {
      const source = form.values.widgets[index]
      if (isNotDefined(source)) return
      const copy = { ...source, widgetId: createId('DashWidget') }
      form.insertListItem('widgets', copy, index + 1)
      openWidget(copy)
    },
    [form, openWidget]
  )

  const copyFromDashboard = useCallback(
    (widget: DashboardWidget, sourceFilter: DashboardFilter | undefined): void => {
      closeCopy()
      // The copy keeps the scope it reported under on its old dashboard, so moving
      // it here doesn't silently change what it measures.
      const copy = { ...widget, widgetId: createId('DashWidget'), filter: widget.filter ?? sourceFilter }
      form.insertListItem('widgets', copy)
      openWidget(copy)
    },
    [closeCopy, form, openWidget]
  )

  const isHustle = dashboard.home === true

  return (
    <Page>
      <Page.Heading back goBackBehavior='history' title={form.values.name?.trim() || t('Dashboard')}>
        <ButtonCancel disabled={isSaving || isRemoving} onClick={cancel} />
        <ButtonSave disabled={!form.isDirty()} loading={isSaving} onClick={save} />
      </Page.Heading>
      <Page.Content>
        <SplitPreviewLayout
          preview={
            <DashboardBuildPreview
              dashboardFilter={form.values.filter}
              dateRange={form.values.dateRange ?? DEFAULT_DASHBOARD_DATE_RANGE}
              widgets={form.values.widgets}
            />
          }
        >
          <Stack gap='xl'>
            <DashboardSettingsSection form={form} />
            {isHustle ? (
              <HustleWidgetSection />
            ) : (
              <DashboardWidgetListSection
                reportColumns={reportColumns}
                widgets={form.values.widgets}
                onAddWidget={() => addWidget('number')}
                onConfigure={openWidget}
                onCopyFromDashboard={openCopy}
                onDuplicate={duplicateWidget}
                onMove={(from, to) => form.reorderListItem('widgets', { from, to })}
                onRemove={(index) => form.removeListItem('widgets', index)}
              />
            )}
            <DangerZone>
              <TButton icon='delete' loading={isRemoving} variant='destructive' onClick={remove}>
                Delete Dashboard
              </TButton>
            </DangerZone>
          </Stack>
        </SplitPreviewLayout>
      </Page.Content>

      <DashboardWidgetCopyPicker
        excludeDashboardId={dashboard.dashboardId}
        opened={isCopyOpen}
        reportColumns={reportColumns}
        onClose={closeCopy}
        onSelect={copyFromDashboard}
      />
      {isDefined(editingWidget) && (
        <WidgetConfigModal key={editingWidget.widgetId} widget={editingWidget} onClose={closeWidget} />
      )}
    </Page>
  )
}

function createWidget(type: DashboardWidgetType): DashboardWidget {
  const base = { widgetId: createId('DashWidget'), metrics: [] }

  switch (type) {
    case 'number':
      return { ...base, type }
    case 'goal':
      return { ...base, type }
    case 'leaderboard':
      return { ...base, type }
    case 'line':
      return { ...base, type }
    case 'table':
      return { ...base, type }
  }
}

export const Route = createFileRoute('/dashboard_/$dashboardId_/edit/')({
  component: DashboardBuild,
})
